import React, { useState, useMemo, useEffect } from 'react';
import Select from 'react-select';
import { X, Briefcase, AlertCircle } from 'lucide-react';
import { useAreas } from '../hooks/useAreas';
import { useOptions } from '../contexts/OptionsContext';
import { colaboradores } from '../utils/stageHelpers';
import { gerarNomeOferta } from '../utils/ofertaHelpers';

/**
 * Modal de criação de nova OFERTA.
 *
 * Props:
 *   empresas[]            — lista de empresas (vem do Dashboard)
 *   onClose()             — fecha o modal
 *   onSave(payload)       — callback que persiste a oferta (recebe payload já validado)
 *   preselectedEmpresaId  — opcional: pré-seleciona uma empresa (ex: ao clicar "Nova Oferta" dentro de uma empresa)
 *
 * Fluxo de Área:
 *   - "Selecionar existente" → dropdown com áreas já cadastradas
 *   - "Criar nova"           → form inline com apenas nome + descrição
 *                              (na criação, chama createArea antes de criar a oferta)
 */
const CreateOfertaModal = ({ empresas = [], onClose, onSave, preselectedEmpresaId = null }) => {
  const { areas, loading: loadingAreas, createArea } = useAreas();
  const { dealStages, loadingOptions } = useOptions();

  const [areaMode, setAreaMode] = useState('existing'); // 'existing' | 'new'
  const [formData, setFormData] = useState({
    empresa_id: preselectedEmpresaId || null,
    area_id: null,
    nova_area_nome: '',
    nova_area_descricao: '',
    nome: '',
    descricao: '',
    status: null,
    responsavel_id: null,
    valor_proposto: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  // Sessão 4.9.2: auto-preenchimento do nome quando empresa + área conhecidas
  const [nomeEditadoManualmente, setNomeEditadoManualmente] = useState(false);

  useEffect(() => {
    if (nomeEditadoManualmente) return;
    const empresaSelecionada = empresas?.find(e => e.id === formData.empresa_id);
    const areaSelecionada = areas?.find(a => a.id === formData.area_id);
    if (empresaSelecionada && areaSelecionada) {
      const sugestao = gerarNomeOferta(empresaSelecionada, areaSelecionada);
      if (sugestao) setFormData(prev => ({ ...prev, nome: sugestao }));
    }
  }, [formData.empresa_id, formData.area_id, empresas, areas, nomeEditadoManualmente]);

  // Opções para react-select
  const empresaOptions = useMemo(
    () => empresas.map(e => ({
      value: e.id,
      label: e.empresa || e.company_name || '(sem nome)'
    })),
    [empresas]
  );

  const areaOptions = useMemo(
    () => areas.map(a => ({
      value: a.id,
      label: a.nome + (a.descricao ? ` — ${a.descricao.slice(0, 40)}${a.descricao.length > 40 ? '…' : ''}` : '')
    })),
    [areas]
  );

  // Filtra o "Todos os Responsáveis" (usado só em filtros de tabela, não em formulário)
  const responsavelOptions = useMemo(
    () => colaboradores.filter(c => c.value !== 'all'),
    []
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg(null);
    setSubmitting(true);

    try {
      if (!formData.empresa_id) throw new Error('Selecione a empresa destinatária');
      if (!formData.nome.trim()) throw new Error('Informe o nome da oferta');
      if (!formData.status) throw new Error('Selecione o status');

      let areaId = formData.area_id;

      if (areaMode === 'new') {
        if (!formData.nova_area_nome.trim()) {
          throw new Error('Informe o nome da nova área');
        }
        const novaArea = await createArea({
          nome: formData.nova_area_nome,
          descricao: formData.nova_area_descricao
        });
        areaId = novaArea.id;
      } else {
        if (!formData.area_id) {
          throw new Error('Selecione uma área existente ou crie uma nova');
        }
      }

      await onSave({
        empresa_id: formData.empresa_id,
        area_id: areaId,
        nome: formData.nome,
        descricao: formData.descricao,
        status: formData.status,
        responsavel_id: formData.responsavel_id,
        valor_proposto: formData.valor_proposto
      });

      onClose();
    } catch (err) {
      console.error('Erro ao criar oferta:', err);
      setErrorMsg(err.message || 'Erro desconhecido ao criar oferta');
    } finally {
      setSubmitting(false);
    }
  };

  const selectPortalStyles = { menuPortal: base => ({ ...base, zIndex: 10001 }) };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10000]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <div className="bg-orange-100 p-2 rounded-lg">
              <Briefcase className="text-orange-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Nova Oferta</h2>
              <p className="text-sm text-slate-500">Vincular uma área a uma empresa</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={24} />
          </button>
        </div>

        {/* Error banner */}
        {errorMsg && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
            <p className="text-sm text-red-700">{errorMsg}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Empresa */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Empresa destinatária *
            </label>
            <Select
              options={empresaOptions}
              value={empresaOptions.find(o => o.value === formData.empresa_id) || null}
              onChange={opt => setFormData({ ...formData, empresa_id: opt?.value || null })}
              placeholder="Selecione a incorporadora..."
              isSearchable
              isDisabled={submitting}
              menuPortalTarget={document.body}
              styles={selectPortalStyles}
            />
          </div>

          {/* Área — modo existente vs nova */}
          <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Área (terreno) *
            </label>
            <div className="flex gap-4 mb-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  checked={areaMode === 'existing'}
                  onChange={() => setAreaMode('existing')}
                  disabled={submitting}
                />
                Selecionar existente
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  checked={areaMode === 'new'}
                  onChange={() => setAreaMode('new')}
                  disabled={submitting}
                />
                Criar nova
              </label>
            </div>

            {areaMode === 'existing' ? (
              <Select
                options={areaOptions}
                value={areaOptions.find(o => o.value === formData.area_id) || null}
                onChange={opt => setFormData({ ...formData, area_id: opt?.value || null })}
                placeholder={
                  loadingAreas
                    ? 'Carregando áreas...'
                    : (areaOptions.length === 0
                      ? 'Nenhuma área cadastrada — crie uma nova'
                      : 'Selecione a área...')
                }
                isSearchable
                isDisabled={submitting || loadingAreas}
                noOptionsMessage={() => 'Nenhuma área encontrada'}
                menuPortalTarget={document.body}
                styles={selectPortalStyles}
              />
            ) : (
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Nome da nova área (ex: Terreno Ruth Cardoso 5.983m²)"
                  value={formData.nova_area_nome}
                  onChange={e => setFormData({ ...formData, nova_area_nome: e.target.value })}
                  className="w-full border rounded-md p-2 text-sm"
                  disabled={submitting}
                />
                <textarea
                  placeholder="Descrição (opcional)"
                  value={formData.nova_area_descricao}
                  onChange={e => setFormData({ ...formData, nova_area_descricao: e.target.value })}
                  className="w-full border rounded-md p-2 text-sm h-16 resize-none"
                  disabled={submitting}
                />
              </div>
            )}
          </div>

          {/* Nome da oferta */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Nome da oferta *
            </label>
            <input
              type="text"
              value={formData.nome}
              onChange={e => {
                setFormData({ ...formData, nome: e.target.value });
                setNomeEditadoManualmente(true);
              }}
              placeholder="Ex: CONX: 043.042 Gama Lobo"
              className="w-full border rounded-md p-2"
              disabled={submitting}
              required
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Descrição da negociação
            </label>
            <textarea
              value={formData.descricao}
              onChange={e => setFormData({ ...formData, descricao: e.target.value })}
              placeholder="Contexto, histórico, particularidades..."
              className="w-full border rounded-md p-2 h-20 resize-none"
              disabled={submitting}
            />
          </div>

          {/* Status + Responsável */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Status *
              </label>
              <Select
                options={dealStages}
                value={dealStages.find(o => o.value === formData.status) || null}
                onChange={opt => setFormData({ ...formData, status: opt?.value || null })}
                placeholder="Estágio inicial..."
                isDisabled={submitting || loadingOptions}
                menuPortalTarget={document.body}
                styles={selectPortalStyles}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Responsável
              </label>
              <Select
                options={responsavelOptions}
                value={responsavelOptions.find(o => o.value === formData.responsavel_id) || null}
                onChange={opt => setFormData({ ...formData, responsavel_id: opt?.value || null })}
                placeholder="Quem cuida..."
                isClearable
                isDisabled={submitting}
                menuPortalTarget={document.body}
                styles={selectPortalStyles}
              />
            </div>
          </div>

          {/* Valor proposto */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Valor proposto (R$)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.valor_proposto}
              onChange={e => setFormData({ ...formData, valor_proposto: e.target.value })}
              placeholder="Ex: 95000000"
              className="w-full border rounded-md p-2"
              disabled={submitting}
            />
            <p className="text-xs text-slate-500 mt-1">
              Opcional — pode ser preenchido depois quando fechar a proposta
            </p>
          </div>

          {/* Footer */}
          <div className="pt-4 flex justify-end gap-3 border-t mt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-md disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Criando...' : 'Criar Oferta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOfertaModal;
